import {
  OreButton,
  OreCardContainer,
  OreCardWrapper,
  OreDivider,
  OreHeading,
  OreStack,
  OreText,
} from '@edenredespana/oreneta'
import { useEffect, useState } from 'react'
import { openNavigationRoutes } from '@/Shared/ui/routers/data/navigationRoutes'
import { Link } from 'react-router-dom'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { LoginTranslation } from '@/Account/ui/accountTranslation'
import LoginBgImage from '@/Account/ui/assets/login-bg.svg'
import { useRateLimitedCountdown } from "@/Shared/ui/hooks"
import { createLoginRepository } from '@/Account/infrastructure/ApiLoginRepository'
import { useLoginController } from '@/Account/ui/hooks'
import { LoginBasicForm } from '@/Account/ui/molecules'
import { LoginForm as LoginFormInterface } from '@/Account/ui/hooks'
import { formTranslation } from '@/Shared/ui/sharedTranslation'
import { HttpMethod } from '@/Shared/domain'
import { login as loginEndpoint } from '@/Shared/infrastructure/endpoints'

export const LoginForm = (): JSX.Element => {
  const { t } = useTranslation()
  const { form, login } = useLoginController(createLoginRepository())
  const { formState: { isValid }, watch } = form

  const {
    formattedMessage,
    startCountdown,
    isBlocked,
    shouldBlockForm,
  } = useRateLimitedCountdown({
    messageTemplate: t(formTranslation.common.validationBlockMessage),
    params: {
      endpointRoute: `/${loginEndpoint}`,
      endpointActionName: HttpMethod.POST
    }
  })

  const username = watch('username')
  const rememberUser = watch('rememberUser')

  const handleSubmit = async (data: LoginFormInterface) => {
    if (rememberUser) {
      localStorage.setItem('rememberedUsername', username ?? '')
    } else {
      localStorage.removeItem('rememberedUsername')
    }

    const success = await login(data)

    if (!success) {
      const mustBlockForm = await shouldBlockForm()
      if (mustBlockForm) {
        form.setError("root", { type: "custom", message: t(formTranslation.errors.wrongUserOrPass) })
        startCountdown()
      }
    }
  }

  useEffect(() => {
    const rememberedUsername = localStorage.getItem('rememberedUsername')
    if (rememberedUsername) {
      form.setValue('username', rememberedUsername)
      form.setValue('rememberUser', true)
    }
  }, [])

  return (
    <FormProvider {...form}>
      <form>
        <OreCardContainer>
          <OreStack space="large" placeContent="stretch">
            <OreStack placeContent="start" placeItems="start" space="medium">
              <OreStack direction="row" space="large" placeItems="stretch">
                <OreCardWrapper space="large">
                  <OreStack space="small" placeContent="stretch">
                    <OreHeading as="h2" size="headline-lg">
                      {t(LoginTranslation.form.pageTitle)}
                    </OreHeading>
                    <LoginBasicForm isBlocked={isBlocked} />
                    <OreStack space="large" placeContent="start">
                      <OreButton
                        disabled={!isValid || isBlocked}
                        type="submit"
                        onClick={form.handleSubmit(handleSubmit)}
                        size="large"
                        category="primary"
                        data-testid="login"
                      >
                        {t(LoginTranslation.form.submitBtn)}
                      </OreButton>
                    </OreStack>
                    {isBlocked && (
                      <OreStack placeItems="center" placeContent="center">
                        <OreText align="center" size="text-xs">
                          {formattedMessage}
                        </OreText>
                      </OreStack>
                    )}
                    <OreText tone="neutral">
                      <Link to={openNavigationRoutes.passwordRecoveryStep1}>{t(LoginTranslation.form.forgotPass)}</Link>
                    </OreText>
                    <OreDivider />
                    <OreText>
                      {t(LoginTranslation.form.createAccount)} <Link to={openNavigationRoutes.registerStepOne}>{t(LoginTranslation.form.createAccountLink)}</Link>
                    </OreText>
                  </OreStack>
                </OreCardWrapper>
                <OreStack placeContent="center" direction="column">
                  <img src={LoginBgImage} alt="" style={{ textAlign: 'right' }} />
                </OreStack>
              </OreStack>
            </OreStack>
          </OreStack>
        </OreCardContainer>
      </form>
    </FormProvider>
  )
}
